// Adding and removing a Wi-Fi network across a DEVICE, a ROOM or a LOCATION.
//
// The shape is deliberately firmwareUpdate.js's: gather the devices in scope,
// split live from offline, then reach each one by every route that exists. It is
// the same problem — most of the estate is asleep most of the time — so it gets
// the same answer rather than a second one.
//
// ── TWO THINGS ARE BOTH CALLED "WI-FI", AND ONLY ONE REACHES A DEVICE ───────
//
//   1. the LOCATION'S RECORD — `AddWifiNameAndWifiPasswordByLocationId`. A row
//      in a table. Writing it changes no device and never has; it is what a
//      replaced or re-flashed device is later provisioned from.
//   2. the DEVICE'S OWN LIST — `ADDWIFIssid=…pass=…` over MQTT. This is the one
//      that puts the credential in the ESP's flash.
//
// Doing only (1) looks like success and joins nothing to the network. Doing only
// (2) works today and is forgotten the next time a device is swapped. So an add
// does BOTH — which is why the caller gets one function per intent rather than
// being trusted to remember the second call.
//
// ── The two routes to a device ─────────────────────────────────────────────
//
//   MQTT            reaches a device connected right now. Dropped, with no
//                   error at either end, for one that is not.
//   PendingCommand  read on the device's ~20 minute poll. This is the only
//                   thing that makes an offline device reachable at all.
//
// ⚠️ THE PARKED STRING IS BARE — NO [uno], NO #userId. Read out of
// OtaForSwitching.ino, not assumed:
//
//   • the poll calls ProcessMessageReceived(TempPendingCommand, 1036) (:1642),
//     the SAME dispatcher MQTT feeds;
//   • that dispatcher skips its upper-casing for these two verbs (:1240), so an
//     SSID keeps its case;
//   • the #userId split lives in the MQTT receive path only (:1715-1718) and is
//     itself skipped for these verbs — the pending path never runs it, so a `#`
//     parked here would be read as part of the password;
//   • a [uno] prefix is stripped only `if present` (:1247), so bare is correct.
//
// The firmware calls exactly this form on itself when its own web page saves a
// network — ProcessMessageReceived("ADDWIFIssid=" + newSsid + "pass=" + newPass)
// (:3204). That is the shape to park.
//
// ⚠️ EVERY DEVICE THAT ACCEPTS THIS REBOOTS. ADDWIFI and REMOVEWIFI both end in
// ESP.restart() (:3198, :3221), so a location-wide run takes the site down and
// brings it back. Hence the spacing below, and the warning before sending.

import { addLocationWifi, updateDeviceInstalled } from '../api/api';
import { publishAddressed } from '../utils/deviceController';
import {
  buildAddWifiMqtt, buildRemoveWifiMqtt, isProtectedSsid,
} from './commands';

// Re-exported so a caller needs one import for the whole flow, and so the
// device-gathering rules (which merge back the types the room endpoint drops)
// are never re-implemented here.
export {
  collectRoomDevices, collectLocationDevices, splitByLiveness,
} from './firmwareUpdate';

const unoOf = (d) => d && (d.UniqueNumber ?? d.uniqueNumber);

/**
 * Gap between publishes.
 *
 * Wider than the firmware rollout's 800 ms on purpose: each device that accepts
 * this reboots and then rejoins the access point. Firing a whole location at
 * once hands the router a stampede of simultaneous associations, which is how a
 * bulk change ends with devices that never came back.
 */
export const WIFI_STEP_DELAY_MS = 1200;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** The bare string parked in PendingCommand. See the note at the top. */
export const wifiPendingCommand = (mode, ssid, password) => (mode === 'remove'
  ? buildRemoveWifiMqtt(ssid, password)
  : buildAddWifiMqtt(ssid, password));

/** The exact bytes MQTT carries, for the confirmation screen to print. */
export const wifiPayload = (mode, ssid, password, uniqueNumber) =>
  `[${uniqueNumber || '<uno>'}]${wifiPendingCommand(mode, ssid, password)}`;

/** Is this parked string one of ours, so replacing it loses nothing? */
export const isWifiCommand = (s) => /^(ADDWIFI|REMOVEWIFI)/i.test(String(s || '').trim());

/**
 * Refuse a request that would strand a device. -> '' when it is fine.
 *
 * ⚠️ The firmware's own fallback networks cannot be removed. On the ESP32 fleet
 * emptying the list does not even survive a reboot — it is reseeded with exactly
 * these — so the operation would report success and change nothing; on the
 * ESP8266 it can genuinely leave a device with no way back onto a network.
 */
export function whyNotAllowed(mode, ssid) {
  const s = String(ssid || '').trim();
  if (!s) return 'Type the network name.';
  if (mode === 'remove' && isProtectedSsid(s)) {
    return `“${s}” is a firmware fallback network and cannot be removed — a device`
      + ' with nothing else stored would have no way back onto a network.';
  }
  return '';
}

/**
 * Publish to each device in turn.
 *
 * Only ever called with the LIVE list: a publish to a device that is not
 * listening is dropped silently, which is what queueWifi is for.
 *
 * ⚠️ `publishAddressed`, NEVER `publishCommand`. These are the two verbs the
 * firmware does not strip `#<userId>` from, so the envelope's user id would end
 * up inside the password — the whole reason REMOVEWIFI never worked. The
 * `[<uno>]` prefix stays: without it the ESP32 treats the command as a
 * broadcast and every device on the topic acts on it.
 */
export async function pushWifi(devices, { mode, ssid, password }, onProgress) {
  const list = (devices || []).filter((d) => unoOf(d));
  const body = wifiPendingCommand(mode, ssid, password);
  for (let i = 0; i < list.length; i += 1) {
    publishAddressed(unoOf(list[i]), body);
    if (onProgress) onProgress(i + 1, list.length, list[i]);
    // eslint-disable-next-line no-await-in-loop
    if (i < list.length - 1) await sleep(WIFI_STEP_DELAY_MS);
  }
  return list.length;
}

/**
 * Park it for devices that are not listening now.
 *
 * -> { queued, skipped } — `skipped` are rows already holding somebody else's
 *    command, with what they hold.
 *
 * ⚠️ THE SLOT IS ONE DEEP AND THE SERVER EMPTIES IT WHEN THE DEVICE READS IT.
 * A row already holding an UPDATE CODE is therefore left alone rather than
 * overwritten: silently discarding a queued firmware rollout in order to add a
 * network is not a trade this is allowed to make by itself. Another Wi-Fi
 * command is ours, and is replaced.
 */
export async function queueWifi(devices, { mode, ssid, password }) {
  const list = (devices || []).filter((d) => unoOf(d));
  const body = wifiPendingCommand(mode, ssid, password);
  let queued = 0;
  const skipped = [];
  await Promise.all(list.map(async (d) => {
    const held = d.PendingCommand;
    if (held && String(held).trim() && !isWifiCommand(held)) {
      skipped.push({ device: d, held: String(held).trim() });
      return;
    }
    try {
      await updateDeviceInstalled(unoOf(d), { PendingCommand: body });
      queued += 1;
    } catch (e) { /* counted by omission — the caller reports queued vs total */ }
  }));
  return { queued, skipped };
}

/**
 * Write the network onto the LOCATION, so a device installed there later can be
 * provisioned from it. -> true when the row was written.
 *
 * ⚠️ THERE IS NO DELETE ENDPOINT FOR THIS LIST. A remove therefore reaches the
 * devices only, and the site keeps the record. The dialog says so rather than
 * implying the location has forgotten the network.
 */
export async function saveLocationWifi(locationId, ssid, password) {
  if (!(Number(locationId) > 0)) return false;
  try {
    await addLocationWifi(locationId, ssid, password);
    return true;
  } catch (e) {
    return false;
  }
}
